<template>
  <div
    v-bind="getThemeBlockWithBackgroundProps('messenger-start-button')"
    class="messenger-start-button"
    :class="[
      isButtonTextVisible ? 'messenger-start-button--with-text' : 'messenger-start-button--icon-only',
      {
        'messenger-start-button--has-notification': hasNotification,
      },
    ]"
    @click="toggleExpand()"
  >
    <widget-icon
      :name="iconRealName"
      :size="iconSize"
    />
    <messenger-text-with-emoji
      v-if="isButtonTextVisible"
      :text="text"
    />
  </div>
</template>

<script setup lang="ts">
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import type { WidgetIconName } from '@widgets/shared/icons/icons'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import { computed } from '@/composition/vue/compositionApi'
import { WebWidgetDetailsMinimizedStateIcon } from '@/data/models/domain/messages/settings/webWidget/types'

const props = withDefaults(
  defineProps<{
    isExpanded?: boolean
    borderRadius?: number
    text?: string
    iconName?: WebWidgetDetailsMinimizedStateIcon
    hasNotification?: boolean
  }>(),
  {
    isExpanded: false,
    borderRadius: 999,
    text: '',
    iconName: WebWidgetDetailsMinimizedStateIcon.TEXTMAGIC,
    hasNotification: false,
  },
)

const emit = defineEmits<{
  (e: 'toggle-expand', newValue: boolean): void
}>()

const { getThemeBlockWithBackgroundProps } = useWidgetTheme()

const isButtonTextVisible = computed(() => {
  return props.text && !props.isExpanded
})

const iconSize = computed(() => {
  if (props.isExpanded) {
    return '32px'
  }
  return props.text ? '20px' : '24px'
})

const iconRealNameMap: Record<WebWidgetDetailsMinimizedStateIcon, WidgetIconName> = {
  textmagic: 'tmi-logo',
  chat: 'forum',
  message: 'tmi-message-bubble',
  phone: 'phone',
  email: 'email',
}

const iconRealName = computed<WidgetIconName>(() => {
  if (props.isExpanded) {
    return 'expand_more'
  }
  return iconRealNameMap[props.iconName]
})

const toggleExpand = () => {
  emit('toggle-expand', !props.isExpanded)
}
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

.messenger-start-button {
  @include widget-gradient-bg(messenger-start-button--gradient, '', true);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 2px 5px 0 rgba($black, 0.3);
  cursor: pointer;
  color: $white;
  font-size: 16px;
  line-height: 24px;
  font-weight: $semi-bold-font;
  border-radius: calc(v-bind('props.borderRadius') * 1px);
  height: 48px;

  &:hover {
    box-shadow: 0 2px 5px 0 rgba($black, 0.35);
  }

  &--with-text {
    padding-left: 20px;
    padding-right: 24px;
  }

  &--icon-only {
    width: 48px;
  }

  &--has-notification {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      border-radius: 999px;
      width: 14px;
      height: 14px;
      background-color: $red;
      border: 1px solid $white;
    }
  }
}
</style>
